import { Plugin } from 'chart.js';

const FONT = '600 12px system-ui, -apple-system, "Segoe UI", sans-serif';

/**
 * Draws the stack total above each column.
 *
 * Per-severity values live in the tooltip and the legend; the total is the one number worth
 * labelling directly. Without it a reader has to add four stacked segments by eye to answer
 * "is this going down?", which is the entire question the chart exists for.
 */
export const stackTotalsPlugin: Plugin<'bar'> = {
  id: 'stackTotals',
  afterDatasetsDraw(chart) {
    const { ctx } = chart;
    const first = chart.getDatasetMeta(0);
    if (!first?.data?.length) return;

    const color = (chart.options.plugins as any)?.stackTotals?.color ?? '#1E164D';

    ctx.save();
    ctx.font = FONT;
    ctx.fillStyle = color;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'bottom';

    first.data.forEach((_bar, index) => {
      let top = Number.POSITIVE_INFINITY;
      let total = 0;

      chart.data.datasets.forEach((_ds, di) => {
        if (!chart.isDatasetVisible(di)) return;
        const element = chart.getDatasetMeta(di).data[index] as any;
        if (!element) return;
        const value = Number(chart.data.datasets[di].data[index] ?? 0);
        total += value;
        if (value > 0) top = Math.min(top, element.y);
      });

      if (!Number.isFinite(top)) return;
      ctx.fillText(String(total), (first.data[index] as any).x, top - 6);
    });

    ctx.restore();
  },
};

/**
 * Labels each line at its final point with the series name and current value.
 *
 * With four series a legend alone makes the reader colour-match between the key and the plot;
 * direct labels put the identity on the mark. Labels are nudged apart only enough to stop them
 * overlapping — past that they would detach from their lines and read as noise, so the legend
 * stays present as the dependable channel.
 */
export const lineEndLabelsPlugin: Plugin<'line'> = {
  id: 'lineEndLabels',
  afterDatasetsDraw(chart) {
    const { ctx, chartArea } = chart;
    const opts = (chart.options.plugins as any)?.lineEndLabels ?? {};
    const color = opts.color ?? '#4A4766';
    const minGap = 13;

    const labels: { y: number; text: string; color: string }[] = [];

    chart.data.datasets.forEach((dataset, di) => {
      if (!chart.isDatasetVisible(di)) return;
      const points = chart.getDatasetMeta(di).data;
      const last = points[points.length - 1] as any;
      if (!last) return;

      const value = Number(dataset.data[dataset.data.length - 1] ?? 0);
      labels.push({
        y: last.y,
        text: `${dataset.label} ${value}`,
        color: (dataset.borderColor as string) ?? color,
      });
    });

    if (labels.length === 0) return;

    // Nudge apart, top-down, so no two labels overlap.
    labels.sort((a, b) => a.y - b.y);
    for (let i = 1; i < labels.length; i++) {
      if (labels[i].y - labels[i - 1].y < minGap) labels[i].y = labels[i - 1].y + minGap;
    }

    ctx.save();
    ctx.font = FONT;
    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';

    labels.forEach(label => {
      // Text wears the ink token, never the series colour — a light hue is illegible as text.
      // The coloured dot beside it carries the identity.
      ctx.fillStyle = label.color;
      ctx.beginPath();
      ctx.arc(chartArea.right + 8, label.y, 3.5, 0, Math.PI * 2);
      ctx.fill();

      ctx.fillStyle = color;
      ctx.fillText(label.text, chartArea.right + 16, label.y);
    });

    ctx.restore();
  },
};

/**
 * Draws a faint upright rule at each delivery.
 *
 * A newly delivered project dumps its whole finding set into the backlog, so the line RISES.
 * Without marking why, those rises read as the client getting worse rather than as a new project
 * arriving. Indices come from `options.plugins.deliveryRules.indices`.
 */
export const deliveryRulesPlugin: Plugin<'line' | 'bar'> = {
  id: 'deliveryRules',
  beforeDatasetsDraw(chart) {
    const opts = (chart.options.plugins as any)?.deliveryRules;
    const indices: number[] = opts?.indices ?? [];
    if (indices.length === 0) return;

    const { ctx, chartArea, scales } = chart;
    const xScale = scales.x;
    if (!xScale) return;

    ctx.save();
    ctx.strokeStyle = opts.color ?? 'rgba(0,0,0,0.10)';
    ctx.lineWidth = 1;

    indices.forEach(index => {
      const x = xScale.getPixelForValue(index);
      if (!Number.isFinite(x)) return;
      ctx.beginPath();
      ctx.moveTo(x, chartArea.top);
      ctx.lineTo(x, chartArea.bottom);
      ctx.stroke();
    });

    ctx.restore();
  },
};
